import React from 'react'
import { LuX } from 'react-icons/lu'

import { AdminUsersHoverCard } from '@/components/misc/AdminUsersHoverCard'
import DirectionAgreements from '@/components/misc/CommercialAgreementsDetails/components/DirectionAgreements'
import { useCommercialAgreementsQuery } from '@/components/misc/CommercialAgreementsDetails/hooks/useCommercialAgreementsQuery'
import NotAllowedToViewContent from '@/components/misc/NotAllowedToViewContent'
import SkeletonBox from '@/components/skeleton/SkeletonBox'
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { usePermissions } from '@/hooks/usePermissions'
import { COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION } from '@/lib/constants'
import {
  hideCommercialAgreementsModal,
  selectCommercialAgreementReferralDirection,
  selectCommercialAgreementsModalPanelId,
} from '@/redux/slices/commercialAgreementsModal'

export interface CommercialAgreementDetailsProps {
  onConfirmAgreement?: () => Promise<void>
  isConfirmAgreementPrompt?: boolean
  onClose?: () => Promise<void>
  header?: string
  showDirectionDescription?: boolean
  variant: 'inline' | 'drawer'
}

const CommercialAgreementsDetails: React.FC<
  CommercialAgreementDetailsProps
> = ({
  onConfirmAgreement,
  isConfirmAgreementPrompt,
  onClose,
  header = 'Review commercial agreements',
  variant,
}) => {
  const referralDirection = useAppSelector(
    selectCommercialAgreementReferralDirection,
  )
  const panelId =
    useAppSelector(selectCommercialAgreementsModalPanelId) ?? undefined
  const dispatch = useAppDispatch()
  const commercialAgreementsQuery = useCommercialAgreementsQuery(panelId)
  const { canManageCommercialAgreements } = usePermissions()

  if (!panelId || !referralDirection) {
    return null
  }

  const isLoading = !commercialAgreementsQuery.data

  function renderModalTitle() {
    return <>{isLoading ? 'Commercial agreements' : header}</>
  }

  function renderModalBody() {
    if (isLoading) {
      return <SkeletonBox style={{ height: '52vh' }} />
    }

    if (isConfirmAgreementPrompt && !canManageCommercialAgreements) {
      return (
        <NotAllowedToViewContent
          message={
            <p>
              You don't have permission to confirm the relationship terms.
              Please contact <AdminUsersHoverCard>an admin</AdminUsersHoverCard>{' '}
              at your company to do this.
            </p>
          }
        />
      )
    }

    const { panel } = commercialAgreementsQuery.data

    return (
      <div className="flex flex-col gap-12">
        {[
          COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING,
          COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.INCOMING,
        ].map((direction) => (
          <DirectionAgreements
            key={direction}
            panel={panel}
            direction={direction}
            onConfirmAgreement={onConfirmAgreement}
            variant={variant}
          />
        ))}
      </div>
    )
  }

  function hideModal() {
    dispatch(hideCommercialAgreementsModal())
  }

  async function handleClose() {
    if (onClose) {
      await onClose()
    }

    hideModal()
  }

  if (variant === 'inline') {
    return <>{renderModalBody()}</>
  }

  return (
    <Drawer open direction="right" onHide={handleClose}>
      <DrawerContent
        className="md:w-220"
        onInteractOutside={(event) => {
          if (!onClose) {
            event.preventDefault()
          }
        }}
      >
        <DrawerHeader
          className="flex flex-row items-center justify-between gap-4"
        >
          <DrawerTitle>{renderModalTitle()}</DrawerTitle>

          {onClose && (
            <DrawerClose asChild>
              <LuX className="text-xl" />
            </DrawerClose>
          )}
        </DrawerHeader>

        <DrawerBody className="pb-12">
          <>{renderModalBody()}</>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default CommercialAgreementsDetails
